export const PERMISSIONS = [
  "users:read",
  "users:write",
  "users:delete",
  "roles:read",
  "roles:write",
  "groups:read",
  "groups:write",
  "logs:read",
  "settings:read",
  "settings:write",
  "clients:read",
  "clients:write",
  "data:export",
  "data:import",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export interface BuiltInRoleDefinition {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
}

const readPermissions = PERMISSIONS.filter((p) => p.endsWith(":read"));

export const BUILT_IN_ROLES: readonly BuiltInRoleDefinition[] = [
  { id: "owner", name: "owner", description: "Full access", permissions: [...PERMISSIONS] },
  {
    id: "admin",
    name: "admin",
    description: "All permissions except data import",
    permissions: PERMISSIONS.filter((p) => p !== "data:import"),
  },
  {
    id: "auditor",
    name: "auditor",
    description: "Read-only access including audit logs",
    permissions: [...readPermissions],
  },
  { id: "staff", name: "staff", description: "Regular employee (no console access)", permissions: [] },
];

export const BUILT_IN_ROLE_IDS = BUILT_IN_ROLES.map((r) => r.id);

export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value);
}

/** A role lookup: role id → permissions. Defaults to built-ins only. */
export type RolePermissionMap = ReadonlyMap<string, readonly Permission[]>;

export function builtInRoleMap(): RolePermissionMap {
  return new Map(BUILT_IN_ROLES.map((r) => [r.id, r.permissions]));
}

/** Union of permissions granted by the given roles. Unknown roles grant nothing. */
export function permissionsOf(
  roles: readonly string[],
  map: RolePermissionMap = builtInRoleMap(),
): Permission[] {
  const out = new Set<Permission>();
  for (const role of roles) for (const p of map.get(role) ?? []) out.add(p);
  return PERMISSIONS.filter((p) => out.has(p));
}

export function can(
  roles: readonly string[],
  permission: Permission,
  map: RolePermissionMap = builtInRoleMap(),
): boolean {
  return roles.some((r) => map.get(r)?.includes(permission) ?? false);
}

/** Whether any of the roles grants a `*:write` / `*:delete` / `data:import` permission (i.e. "admin-ish"). */
export function hasWritePermission(
  roles: readonly string[],
  map: RolePermissionMap = builtInRoleMap(),
): boolean {
  return permissionsOf(roles, map).some((p) => !p.endsWith(":read") && p !== "data:export");
}

/** Whether the user may open the console at all (any permission). */
export function hasConsoleAccess(
  roles: readonly string[],
  map: RolePermissionMap = builtInRoleMap(),
): boolean {
  return permissionsOf(roles, map).length > 0;
}
